import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CarSearchSelect } from "../../../components/Search/CarsSearchSelect";
import ClientSearchSelect from "../../../components/Search/ClientSearchSelect";
import MultiServiceSelect from "../../../components/Search/MultiServiceSelect";
import TextInput from "../../../components/inputs/TextInput";
import DatePickerInput from "../../../components/inputs/DatePickerInput"; 
import { addWorkOrder, attachServices, attachComplexServices } from "../../../components/http";
import dayjs from "dayjs"; 
import styles from "./AddWorkOrder.module.scss";
import { FaCarAlt } from "react-icons/fa";
import { IoMdPerson } from "react-icons/io";

const AddWorkOrder = () => {
  const { t } = useTranslation("");
  const currentDateTime = dayjs(); 

  const [formData, setFormData] = useState({
    car_id: "",
    car: { manufacturer: "", model: "", reg_number: "" },
    client_id: "",
    client: { fullname: "" },
    comment: "",
    status: "upcoming",
    documents: [],
    tax: 0,
    start_at: currentDateTime,
    end_at: null,
    issued_on: currentDateTime,
    selectedServices: [],
  });

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCarSelect = (carId, carLabel) => {
    const [manufacturer, model, reg_number] = carLabel.split(" ");
    setFormData((prev) => ({
      ...prev,
      car_id: carId, 
      car: { manufacturer, model, reg_number },
      client_id: "", 
      client: { fullname: "" },
    }));
  };

  const handleClientSelect = (clientId, clientFullname) => {
    setFormData((prev) => ({
      ...prev,
      client_id: clientId,
      client: { fullname: clientFullname },
    }));
  };

  const handleServicesChange = (services) => {
    setFormData((prev) => ({
      ...prev,
      selectedServices: services,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.car_id) {
        alert(t("workOrder.add.errorMissingFields"));
        return;
      }
      const workOrderData = {
        car_id: formData.car_id,
        client_id: formData.client_id,
        comment: formData.comment || "",
        status: formData.status || "upcoming",
        documents: formData.documents.length > 0 ? formData.documents : [],
        tax: formData.tax || 0,
        start_at: formData.start_at ? dayjs(formData.start_at).toISOString() : null,
        end_at: formData.end_at ? dayjs(formData.end_at).toISOString() : null,
      };

      const response = await addWorkOrder(workOrderData);
      const workOrderId = response.work_order_id;

      const regularServices = formData.selectedServices
        .filter((service) => !service.is_complex)
        .map((service) => service.id);

      const complexServices = formData.selectedServices
        .filter((service) => service.is_complex)
        .map((service) => service.id);

      if (regularServices.length > 0) {
        await attachServices({ work_order_id: workOrderId, ids: regularServices });
      }

      if (complexServices.length > 0) {
        await attachComplexServices({ work_order_id: workOrderId, ids: complexServices });
      }

      alert(t("workOrder.add.successMessage"));
    } catch (error) {
      console.error(t("workOrder.add.errorSaving"), error);
      alert(t("workOrder.add.errorMessage"));
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>{t("workOrder.add.title")}</h2>

      <div className={styles.clientSelect}>
        <span className={styles.box}>
          <span className={styles.icon}>
            <FaCarAlt />
          </span>          
          <CarSearchSelect
            onCarSelect={handleCarSelect}
            placeholder={t("workOrder.add.selectCar")}
            currentCar={{
              id: formData.car_id,
              label: `${formData.car.manufacturer} ${formData.car.model} (${formData.car.reg_number})`,
            }}
          />
        </span>
        <span className={styles.box}>
          <span className={styles.icon}>
            <IoMdPerson />
          </span>  
          <ClientSearchSelect
            onClientSelect={handleClientSelect}
            placeholder={t("workOrder.add.selectClient")}
            carId={formData.car_id} 
            currentClient={{
              id: formData.client_id,
              fullname: formData.client.fullname,
            }}
          />
        </span>
      </div>
       
      <div className={styles.mainInfo}>
        <MultiServiceSelect onServicesChange={handleServicesChange} placeholder={t("workOrder.add.selectServices")} />

        <div className={styles.timePicker}>
          <span>
            <label className={styles.label}>{t("workOrder.add.startTime")}:</label>
            <DatePickerInput
              selectedDate={formData.start_at}
              onDateChange={(date) => handleFieldChange("start_at", dayjs(date))}
            />
          </span>      
          <span>
            <label className={styles.label}>{t("workOrder.add.endTime")}:</label>
            <DatePickerInput
              selectedDate={formData.end_at}
              onDateChange={(date) => handleFieldChange("end_at", dayjs(date))}
            />
          </span>
        </div>

        <label className={styles.label}>{t("workOrder.add.comment")}:</label>
        <TextInput
          value={formData.comment}
          onChange={(e) => handleFieldChange("comment", e.target.value)}
          placeholder={t("workOrder.add.enterComment")}
        />

        <label className={styles.label}>{t("workOrder.add.status")}:</label>
        <select
          value={formData.status}
          onChange={(e) => handleFieldChange("status", e.target.value)}
          className={styles.inputContainer}
        >
          <option value="upcoming">{t("workOrder.add.statusUpcoming")}</option>
          <option value="in process">{t("workOrder.add.statusInProcess")}</option>
        </select>

        <label className={styles.label}>{t("workOrder.add.tax")}:</label>
        <input
          type="number"
          value={formData.tax}
          onChange={(e) => handleFieldChange("tax", parseFloat(e.target.value))}
          className={styles.inputContainer}
        />
      </div>

      <button onClick={handleSubmit} className={styles.submitButton}>
        {t("workOrder.add.create")}
      </button>
    </div>
  );
};

export default AddWorkOrder;
